import gsap from 'gsap';

const COMPOSE_EASE = 'power3.inOut';
const WORD_EASE = 'power2.out';
const LOGO_FADE_SCALE = 0.94;
/** Hold the logo fully visible before it rises and fades out. */
const LOGO_HOLD = 0.15;

function resolveStackShiftY() {
  return window.matchMedia('(min-width: 768px)').matches ? -40 : -32;
}

function resolveLogo(stack: HTMLElement) {
  return stack.querySelector<HTMLElement>('.chat-welcome__logo');
}

export type ChatWelcomeComposeElements = {
  stack: HTMLElement;
  titleShell: HTMLElement;
  words: HTMLElement[];
};

export function resetChatWelcomeCompose(elements: ChatWelcomeComposeElements) {
  const { stack, titleShell, words } = elements;
  const logo = resolveLogo(stack);

  gsap.killTweensOf([stack, logo, titleShell, ...words].filter(Boolean));
  gsap.set(stack, { clearProps: 'transform' });
  if (logo) {
    gsap.set(logo, { clearProps: 'all' });
  }
  gsap.set(titleShell, { clearProps: 'all' });
  gsap.set(words, { clearProps: 'all' });
}

export function setChatWelcomeComposeFinalState(elements: ChatWelcomeComposeElements) {
  const { stack, titleShell, words } = elements;
  const logo = resolveLogo(stack);

  gsap.set(stack, { clearProps: 'transform' });
  if (logo) {
    gsap.set(logo, {
      opacity: 0,
      height: 0,
      scale: LOGO_FADE_SCALE,
      overflow: 'hidden',
      visibility: 'hidden',
      pointerEvents: 'none',
      y: 0,
    });
  }
  gsap.set(titleShell, {
    height: 'auto',
    opacity: 1,
    marginTop: 0,
    overflow: 'visible',
  });
  gsap.set(words, { opacity: 1, y: 0, filter: 'blur(0px)' });
}

function measureTitleShellHeight(titleShell: HTMLElement) {
  const previousHeight = titleShell.style.height;
  const previousOverflow = titleShell.style.overflow;
  const previousVisibility = titleShell.style.visibility;
  const previousPosition = titleShell.style.position;

  titleShell.style.height = 'auto';
  titleShell.style.overflow = 'visible';
  titleShell.style.visibility = 'hidden';
  titleShell.style.position = 'absolute';

  const measuredHeight = titleShell.offsetHeight;

  titleShell.style.height = previousHeight;
  titleShell.style.overflow = previousOverflow;
  titleShell.style.visibility = previousVisibility;
  titleShell.style.position = previousPosition;

  return measuredHeight;
}

export function playChatWelcomeCompose(
  elements: ChatWelcomeComposeElements,
  timelineRef: { current: gsap.core.Timeline | null },
) {
  const { stack, titleShell, words } = elements;
  const logo = resolveLogo(stack);

  timelineRef.current?.kill();

  const titleHeight = measureTitleShellHeight(titleShell);
  const stackShiftY = resolveStackShiftY();
  const logoHeight = logo?.offsetHeight ?? 0;

  gsap.set(stack, { clearProps: 'transform' });
  if (logo) {
    gsap.set(logo, {
      opacity: 1,
      y: 0,
      scale: 1,
      height: logoHeight,
      overflow: 'hidden',
      visibility: 'visible',
      pointerEvents: 'auto',
      force3D: true,
      transformOrigin: '50% 50%',
    });
  }
  gsap.set(titleShell, {
    height: 0,
    opacity: 0,
    marginTop: 0,
    overflow: 'hidden',
  });
  gsap.set(words, {
    opacity: 0,
    y: 20,
    filter: 'blur(14px)',
    force3D: true,
  });

  const timeline = gsap.timeline({
    onComplete: () => {
      if (logo) {
        gsap.set(logo, {
          opacity: 0,
          height: 0,
          overflow: 'hidden',
          visibility: 'hidden',
          pointerEvents: 'none',
          clearProps: 'transform,willChange',
        });
      }
      gsap.set(titleShell, { height: 'auto', clearProps: 'overflow' });
      gsap.set(words, { clearProps: 'filter,willChange' });
    },
  });

  timelineRef.current = timeline;

  if (logo) {
    timeline.to(
      logo,
      {
        y: stackShiftY,
        opacity: 0,
        scale: LOGO_FADE_SCALE,
        duration: 1.2,
        ease: COMPOSE_EASE,
      },
      LOGO_HOLD,
    );

    timeline.to(
      logo,
      {
        height: 0,
        duration: 1.0,
        ease: COMPOSE_EASE,
      },
      LOGO_HOLD + 0.28,
    );
  }

  timeline.to(
    titleShell,
    {
      height: titleHeight,
      opacity: 1,
      marginTop: 0,
      duration: 1.15,
      ease: COMPOSE_EASE,
    },
    LOGO_HOLD + 0.14,
  );

  timeline.to(
    words,
    {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: 0.95,
      stagger: {
        each: 0.085,
        ease: 'power1.out',
      },
      ease: WORD_EASE,
    },
    LOGO_HOLD + 0.34,
  );

  return timeline;
}
